<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesProgressBlocks from './SalesProgressBlocks.vue';
import SalesTransactionActions from './SalesTransactionActions.vue';
import SalesTransactionListCells from './SalesTransactionListCells.vue';

const props = defineProps({
  activeList: { type: String, required: true },
  busyAction: { type: String, default: '' },
  showManager: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});

const emit = defineEmits([
  'archive',
  'details',
  'deliver',
  'finalize',
  'register',
  'restore',
  'review',
  'signatures',
]);
const { t } = useI18n();
const contextualList = computed(() =>
  ['revisoes', 'entregues', 'finalizados'].includes(props.activeList)
);
const stageLabel = stage => {
  const labels = {
    rascunho: t('SALES.STATUS.DRAFT'),
    revisao: t('SALES.STATUS.REVIEW'),
    entregue: t('SALES.STATUS.DELIVERED'),
    arquivado: t('SALES.STATUS.TRASH'),
    finalizado: t('SALES.STATUS.FINISHED'),
  };
  return labels[stage] || labels.rascunho;
};
const stageClass = stage =>
  ({
    rascunho: 'bg-amber-50 text-amber-700',
    revisao: 'bg-violet-50 text-violet-700',
    entregue: 'bg-blue-50 text-blue-700',
    arquivado: 'bg-slate-100 text-slate-600',
    finalizado: 'bg-emerald-50 text-emerald-700',
  })[stage] || 'bg-lais-bg-soft text-lais-text-muted';
</script>

<template>
  <tr
    class="border-b border-lais-border-soft align-top transition hover:bg-lais-primary-50/20"
  >
    <td v-if="showManager" class="p-4 text-lais-text-body">
      {{ transaction.manager?.name || t('SALES.COMMON.UNASSIGNED') }}
    </td>
    <td class="p-4">
      <button
        type="button"
        class="text-left"
        @click="emit('details', transaction)"
      >
        <span
          class="block font-semibold text-lais-text hover:text-lais-primary"
        >
          {{ transaction.title || transaction.reference }}
        </span>
        <span class="mt-1 block text-xs text-lais-text-muted">
          {{
            transaction.address?.endereco ||
            t('SALES.COMMON.ADDRESS_UNAVAILABLE')
          }}
        </span>
        <span
          v-if="transaction.reference"
          class="mt-1 block text-xs text-lais-text-muted"
        >
          {{ transaction.reference }}
        </span>
      </button>
    </td>
    <SalesTransactionListCells
      v-if="contextualList"
      :active-list="activeList"
      :transaction="transaction"
    />
    <template v-else>
      <td class="p-4">
        <SalesProgressBlocks
          :progress="transaction.progress"
          :blocks="transaction.block_progress"
        />
      </td>
      <td class="p-4">
        <span
          class="rounded-full px-2 py-1 text-xs font-bold"
          :class="stageClass(transaction.sales_stage)"
        >
          {{ stageLabel(transaction.sales_stage) }}
        </span>
      </td>
    </template>
    <td class="p-4">
      <SalesTransactionActions
        :transaction="transaction"
        :busy-action="busyAction"
        @archive="emit('archive', transaction)"
        @details="emit('details', transaction)"
        @deliver="emit('deliver', transaction)"
        @finalize="emit('finalize', transaction)"
        @register="emit('register', transaction)"
        @restore="emit('restore', transaction)"
        @review="emit('review', transaction)"
        @signatures="emit('signatures', transaction)"
      />
    </td>
  </tr>
</template>
